import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  Target,
  TrendingUp,
  CheckCircle,
  Users,
  Search,
  Sparkles,
  ArrowLeft,
  Download
} from 'lucide-react';
import Card from '../../components/Common/Card';
import SummaryCard from '../../components/Common/SummaryCard';
import api from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import * as XLSX from 'xlsx';

// Custom Tooltip for the Recharts Career Path selection
const CareerPathTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#0b1329] border border-[#1a3884]/60 p-4 rounded-2xl shadow-2xl text-white backdrop-blur-md min-w-[210px] select-none">
        <p className="font-extrabold text-sm text-[#5a6ef6] mb-2">{data.role}</p>
        <div className="space-y-1.5 text-xs">
          <p className="flex justify-between gap-6">
            <span className="text-gray-300 font-semibold">Primary Choice:</span>
            <span className="font-black text-emerald-400">{data.primaryCount} students</span>
          </p>
          <p className="flex justify-between gap-6">
            <span className="text-gray-300 font-semibold">Secondary Choice:</span>
            <span className="font-black text-[#4f7dff]">{data.secondaryCount} students</span>
          </p>
          <p className="flex justify-between gap-6">
            <span className="text-gray-300 font-semibold">Tertiary Choice:</span>
            <span className="font-black text-amber-300">{data.tertiaryCount} students</span>
          </p>
          <hr className="border-white/10 my-2" />
          <p className="flex justify-between gap-6 font-extrabold text-sm">
            <span className="text-gray-150">Total Selected:</span>
            <span className="font-black text-white">{data.totalCount} students</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const CareerAgentAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const { searchQuery, setSearchQuery, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('distribution');
  // Admin-only: skills recruiters typed via "Add Manually" on job postings that
  // are not in the SMAART library. null = not fetched yet (loads on first open).
  const [manualSkills, setManualSkills] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [promotingSkill, setPromotingSkill] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchColleges();
    }
  }, [user]);

  useEffect(() => {
    fetchDepartments();
  }, [selectedCollege, user]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedCollege, selectedDepartment]);

  // Loaded lazily the first time the admin opens the Manual Skills tab.
  useEffect(() => {
    if (activeTab !== 'manual' || user?.role !== 'admin' || manualSkills !== null) return;
    const fetchManualSkills = async () => {
      try {
        setManualLoading(true);
        const { data } = await api.get('/skillslist/manual-skills');
        if (data.success) setManualSkills(data.data || []);
      } catch (error) {
        console.error('Error fetching manual skills:', error);
        toast.error('Failed to load manually added skills');
        setManualSkills([]);
      } finally {
        setManualLoading(false);
      }
    };
    fetchManualSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user, manualSkills]);

  // Promote a recruiter-typed skill into the SMAART library. Once promoted it
  // stops being "manual", so the row is dropped from the list immediately.
  const handlePromoteSkill = async (skillName) => {
    try {
      setPromotingSkill(skillName);
      const { data } = await api.post('/skillslist', { name: skillName });
      if (data.success) {
        toast.success(data.alreadyExists ? `"${skillName}" is already in the library` : `"${skillName}" added to the SMAART library`);
        setManualSkills(prev => (prev || []).filter(s => s.skillName !== skillName));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add skill to library');
    } finally {
      setPromotingSkill(null);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await api.get('/colleges?limit=1000');
      if (response.data.success) {
        setColleges(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const collegeId = user?.role === 'admin' ? selectedCollege : user?.college;
      const url = collegeId ? `/college-degrees?college=${collegeId}` : '/college-degrees';
      const response = await api.get(url);
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = [];
      if (selectedCollege) params.push(`college=${encodeURIComponent(selectedCollege)}`);
      if (selectedDepartment) params.push(`department=${encodeURIComponent(selectedDepartment)}`);

      const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
      const url = `/student-progress/analytics/career-agent${queryStr}`;
      const response = await api.get(url);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching career agent analytics:', error);
      toast.error('Failed to load Career Agent analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    try {
      if (!filteredStudents || filteredStudents.length === 0) {
        toast.error('No data available to export');
        return;
      }

      const excelData = filteredStudents.map((row) => ({
        'Student Name': row.fullName || 'N/A',
        'Roll Number': row.rollNumber || 'N/A',
        'Primary Choice': row.primaryChoice || 'Not Selected',
        'Secondary Choice': row.secondaryChoice || 'Not Selected',
        'Tertiary Choice': row.tertiaryChoice || 'Not Selected',
        'Onboarding Status': row.isOnboarded ? 'Onboarded' : 'Not Onboarded'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Student Choices');

      const collegeObj = colleges.find(c => c._id === selectedCollege);
      const collegeName = collegeObj ? collegeObj.collegeName : 'All';
      const deptName = selectedDepartment || 'All';
      const filename = `student_choices_${collegeName.replace(/\s+/g, '_')}_${deptName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}`;

      XLSX.writeFile(wb, `${filename}.xlsx`);
      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4">
        <div className="w-10 h-10 border-4 border-blue-100 border-t-[#1a3884] rounded-full animate-spin" />
        <p className="text-gray-550 font-bold text-sm animate-pulse">Analyzing student locked career choices...</p>
      </div>
    );
  }

  const { pathways = [], onboardingRate = 0, onboardedStudents = 0, totalStudents = 0, avgReadiness = 0, studentsList = [] } = analytics || {};

  // Filter out pathways/roles with 0 students
  const activePathways = pathways.filter(p => p.totalCount > 0);

  // Filter pathways for display
  const filteredPathways = activePathways.filter(p =>
    p.role.toLowerCase().includes((debouncedSearch || searchQuery || '').toLowerCase())
  );

  // Filter students for display
  const filteredStudents = studentsList.filter(s =>
    s.fullName.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
    s.rollNumber.toLowerCase().includes(studentSearchQuery.toLowerCase())
  );

  // High contrast tick and border colors based on theme
  const tickColor = isDark ? '#cbd5e1' : '#0f172a';
  const strokeColor = isDark ? '#334155' : '#cbd5e1';
  const gridColor = isDark ? '#1e293b' : '#f1f5f9';

  return (
    <div className="space-y-6 text-left">
      {/* Header Section */}
      <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-0.5">
            <h1 className="text-[22px] font-black text-[var(--placement-primary)] tracking-tight">
              Career Agent Analytics
            </h1>
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <Target className="w-4 h-4 text-[var(--placement-primary)] opacity-60" />
              Student distribution across locked career roles and pathways
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {/* Institute Filter (Super Admin only) */}
          {user?.role === 'admin' && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 dark:text-gray-400">Institute:</span>
              <select
                value={selectedCollege}
                onChange={(e) => {
                  setSelectedCollege(e.target.value);
                  setSelectedDepartment(''); // Reset department selection when college changes
                }}
                className="px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] font-bold"
              >
                <option value="">All Institutes</option>
                {colleges.map((college) => (
                  <option key={college._id} value={college._id}>
                    {college.collegeName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Department/Degree Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">Degree:</span>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] font-bold"
            >
              <option value="">All Degrees</option>
              {departments.map((dept, idx) => {
                if (typeof dept === 'string') {
                  return (
                    <option key={idx} value={dept}>
                      {dept}
                    </option>
                  );
                }
                const degreeName = `${dept.fullName} - ${dept.specialization || 'General'}`;
                return (
                  <option key={idx} value={degreeName}>
                    {degreeName}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={handleExportExcel}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black transition-all border-0 shadow-md text-white ${
              isDark
                ? 'bg-gradient-to-r from-[#284BB3] to-[#4F7DFF] hover:shadow-lg hover:opacity-90'
                : 'bg-gradient-to-r from-[#1a3884] to-[#284BB3] hover:shadow-lg hover:opacity-90'
            }`}
          >
            <Download size={14} />
            Export All
          </button>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SummaryCard
          title="Career Onboarding Rate"
          value={`${onboardingRate}%`}
          subtitle={`${onboardedStudents} of ${totalStudents} students`}
          icon={CheckCircle}
          layout="column"
        />
        <SummaryCard
          title="Target Pathways Unlocked"
          value={pathways.filter(p => p.totalCount > 0).length}
          subtitle="Unique roles locked"
          icon={Sparkles}
          layout="column"
        />
      </div>

      {/* Tabs Control */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm p-4 w-full flex justify-center">
        <div className="flex items-center gap-2.5 p-1.5 bg-gray-50 dark:bg-[#001f40]/80 rounded-[1.25rem] border border-gray-100 dark:border-[#1a3884]/20 shadow-sm overflow-x-auto w-full max-w-4xl">
          {[
            { id: 'distribution', label: 'Career Choice Distribution' },
            { id: 'roles', label: 'Role-wise Student Choices' },
            { id: 'students', label: 'Student Choices' },
            // Admin only — skills recruiters typed manually on job postings.
            ...(user?.role === 'admin' ? [{ id: 'manual', label: 'Unlisted Skills' }] : [])
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center justify-center px-9 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 z-10 whitespace-nowrap flex-1 ${activeTab === tab.id
                ? 'bg-[var(--placement-primary)] text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-[#1a3884]/20'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Contents */}
      {activeTab === 'distribution' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pathway Popularity Chart */}
          <Card className="lg:col-span-3 bg-white dark:bg-[#002147] rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-[#1a3884]/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="font-black text-[16px] text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#3956f1] dark:text-[#5a6ef6]" />
                  Career Choice Distribution
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">Hover over any role to see student choice allocations</p>
              </div>
            </div>

            <div style={{ height: `${Math.max(380, activePathways.length * 48)}px` }} className="w-full">
              {activePathways.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={activePathways}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 30, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="barGrad-0" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#93c5fd" />
                        <stop offset="100%" stopColor="#c7d2fe" />
                      </linearGradient>
                      <linearGradient id="barGrad-1" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#a7f3d0" />
                        <stop offset="100%" stopColor="#6ee7b7" />
                      </linearGradient>
                      <linearGradient id="barGrad-2" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#fde68a" />
                        <stop offset="100%" stopColor="#fbcfe8" />
                      </linearGradient>
                      <linearGradient id="barGrad-3" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#ddd6fe" />
                        <stop offset="100%" stopColor="#f3e8ff" />
                      </linearGradient>
                      <linearGradient id="barGrad-4" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#fbcfe8" />
                        <stop offset="100%" stopColor="#fda4af" />
                      </linearGradient>
                      <linearGradient id="barGrad-5" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#a5f3fc" />
                        <stop offset="100%" stopColor="#e0f2fe" />
                      </linearGradient>
                      <linearGradient id="barGrad-6" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#fda4af" />
                        <stop offset="100%" stopColor="#fecdd3" />
                      </linearGradient>
                      <linearGradient id="barGrad-7" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#86efac" />
                        <stop offset="100%" stopColor="#a7f3d0" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis
                      type="number"
                      stroke={strokeColor}
                      tick={{ fill: tickColor, fontSize: 11, fontWeight: 700 }}
                    />
                    <YAxis
                      dataKey="role"
                      type="category"
                      stroke={strokeColor}
                      tick={{ fill: tickColor, fontSize: 11, fontWeight: 700 }}
                      width={180}
                    />
                    <Tooltip content={<CareerPathTooltip />} cursor={{ fill: 'rgba(57,86,241,0.03)' }} />
                    <Bar dataKey="totalCount" radius={[0, 8, 8, 0]} maxBarSize={22}>
                      {activePathways.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`url(#barGrad-${index % 8})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">No data available</div>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'roles' && (
        <Card className="shadow-sm overflow-hidden bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-xl [&>div]:p-0">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-[#1a3884]/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h4 className="font-black text-[16px] text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[var(--placement-primary)] dark:text-[#5a6ef6]" />
                Role-wise Student Choices
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Detailed list of all career paths with student interest counts</p>
            </div>

            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
              <input
                type="text"
                placeholder="Search career role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-[#001f40]/50 border-b border-gray-200 dark:border-[#1a3884]/30">
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Career Role</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Primary</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Secondary</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tertiary</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/20">
                {filteredPathways.length > 0 ? (
                  filteredPathways.map((row, idx) => {
                    return (
                      <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/10 transition-colors">
                        <td className="px-6 py-3.5 align-middle">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{row.role}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{row.primaryCount}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className="text-sm font-bold text-[var(--placement-primary)] dark:text-[#4f7dff]">{row.secondaryCount}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{row.tertiaryCount}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-xs text-gray-500 font-semibold">
                      No matching career role found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === 'students' && (
        <Card className="shadow-sm overflow-hidden bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-xl [&>div]:p-0">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-[#1a3884]/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h4 className="font-black text-[16px] text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[var(--placement-primary)] dark:text-[#5a6ef6]" />
                Student Choices
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Individual student locked career path selections</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] active:scale-95 rounded-xl shadow-md transition-all whitespace-nowrap w-full sm:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              <div className="relative max-w-xs w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search student or roll number..."
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-[#001f40]/50 border-b border-gray-200 dark:border-[#1a3884]/30">
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student Name</th>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Roll Number</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Primary Choice</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Secondary Choice</th>
                  <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tertiary Choice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/20">
                {filteredStudents.length > 0 ? (
                  filteredStudents.map((row, idx) => {
                    return (
                      <tr key={row.id || idx} className="hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/10 transition-colors">
                        <td className="px-6 py-3.5 align-middle">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{row.fullName}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle">
                          <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{row.rollNumber}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${row.primaryChoice === 'Not Selected'
                            ? 'text-gray-400 bg-gray-100 dark:bg-gray-800/30'
                            : 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/20'
                            }`}>{row.primaryChoice}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${row.secondaryChoice === 'Not Selected'
                            ? 'text-gray-400 bg-gray-100 dark:bg-gray-800/30'
                            : 'text-blue-700 bg-blue-50 dark:text-[#4f7dff] dark:bg-blue-950/20'
                            }`}>{row.secondaryChoice}</span>
                        </td>
                        <td className="px-6 py-3.5 align-middle text-center">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${row.tertiaryChoice === 'Not Selected'
                            ? 'text-gray-400 bg-gray-100 dark:bg-gray-800/30'
                            : 'text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/20'
                            }`}>{row.tertiaryChoice}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-xs text-gray-500 font-semibold">
                      No matching student choices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Manual Skills — admin review of skills typed via "Add Manually" on job
          postings that are not in the SMAART library. Promoting one adds it to
          the library so every picker offers it from then on. */}
      {activeTab === 'manual' && user?.role === 'admin' && (
        <Card title={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">Unlisted Skills</span>
              <p className="text-xs font-normal text-gray-400 dark:text-slate-400 mt-0.5">
                Skills recruiters typed by hand on job postings that aren't in the SMAART library yet. Promote the genuine ones so they become matchable.
              </p>
            </div>
            {Array.isArray(manualSkills) && manualSkills.length > 0 && (
              <span className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-lg bg-[#1a3884]/10 dark:bg-[#1a3884]/30 text-[#1a3884] dark:text-[#4f7dff]">
                {manualSkills.length} pending
              </span>
            )}
          </div>
        }>
          {manualLoading || manualSkills === null ? (
            <div className="flex items-center justify-center py-14">
              <div className="w-7 h-7 rounded-full animate-spin" style={{ border: '2.5px solid rgba(26,56,132,0.15)', borderTopColor: '#1a3884' }} />
            </div>
          ) : manualSkills.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-gray-200 dark:border-[#1a3884]/30 rounded-xl">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-3" />
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">No unlisted skills pending</p>
              <p className="text-xs text-gray-400 dark:text-slate-400 mt-1">Every skill recruiters have typed on job postings is already in the SMAART library.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider border-b border-gray-100 dark:border-[#1a3884]/25">
                  <tr>
                    <th className="py-3 px-4 w-[24%]">Skill</th>
                    <th className="py-3 px-3 w-[9%] whitespace-nowrap">Used In</th>
                    <th className="py-3 px-3 w-[18%]">Source</th>
                    <th className="py-3 px-3">Seen On Postings</th>
                    <th className="py-3 px-4 text-right whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#1a3884]/20">
                  {manualSkills.map((skill) => (
                    <tr key={skill.skillName} className="hover:bg-slate-50/70 dark:hover:bg-[#1a3884]/10 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-semibold text-gray-900 dark:text-white">{skill.skillName}</span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-[#1a3884]/5 dark:bg-[#1a3884]/25 text-[#1a3884] dark:text-[#4f7dff]">
                          {skill.count} job{skill.count === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-wrap gap-1">
                          {(skill.sources || []).map((src) => (
                            <span key={src} className="text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize bg-[#1a3884]/5 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] border border-[#1a3884]/10 dark:border-[#4f7dff]/20">
                              {src === 'smaart' ? 'SMAART' : src}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-xs text-gray-500 dark:text-slate-400 line-clamp-1" title={skill.jobs.map(j => `${j.title} — ${j.postedBy}`).join('\n')}>
                          {skill.jobs.slice(0, 3).map(j => j.title).join(', ')}
                          {skill.jobs.length > 3 && ` +${skill.jobs.length - 3} more`}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          disabled={promotingSkill === skill.skillName}
                          onClick={() => handlePromoteSkill(skill.skillName)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1a3884] text-white hover:bg-[#0f225c] disabled:opacity-50 transition-colors"
                        >
                          {promotingSkill === skill.skillName ? 'Adding…' : 'Add to Library'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default CareerAgentAnalytics;
